import { FC, useEffect, useState, useCallback, useContext } from "react";

import { Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subtitle, Title } from "capitalrx-components-2.0/lib/typography";
import { Collapse, CollapseGroup, Link, Message, Messages, Spinner, Tag } from "capitalrx-components-2.0/lib/general";
import { formatDate } from "capitalrx-utilities";

import { getProviderManagementAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { BackLink } from "@/components";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeURL } from "@/utils";


import {
  checkCompleteContactInformation,
  checkCompleteDetails,
  checkCompleteDocuments,
  checkCompletePaymentRemittance,
  ContactInformation,
  Details,
  PaymentRemittance,
  ProviderSubmit,
} from "./components";
import { ProviderTabs } from "./tabComponents/ProviderTabs";
import styles from "./ProviderUpdate.module.scss";


const CompleteTag = <Tag type="success-high-contrast">Complete</Tag>;

export const ProviderUpdate: FC = () => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const { primary_color } = useContext(ThemeSelectorContext);
  const { isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const [paymentPreference, setPaymentPreference] = useState<ProviderManagement.PaymentPreferenceSchema>();
  const [documents, setDocuments] = useState<ProviderManagement.DocumentSchema[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, onExpandedIdsChange] = useState<ReadonlyArray<string>>([
    "details",
    "contact_information",
    "payment_remittance",
    "documents",
  ]);
  const [completeTabs, setCompleteTabs] = useState({
    details: false,
    contact_information: false,
    payment_remittance: false,
    documents: false,
  });

  useEffect(() => {
    getProvider();
  }, []);

  useEffect(() => {
    if (!isLoading && provider) {
      // figure out which tabs are completed on load
      const completed = {
        details: checkCompleteDetails(provider),
        contact_information: checkCompleteContactInformation(provider),
        payment_remittance: checkCompletePaymentRemittance(paymentPreference),
        documents: checkCompleteDocuments(documents),
      };
      setCompleteTabs(completed);

      // close the completed ones on load
      onExpandedIdsChange(
        Object.entries(completed).filter(([_, value]) => !value).map(([key, _]) => key),
      );
    }
  }, [provider, paymentPreference, isLoading]);

  const getProvider = useCallback(async () => {
    setIsLoading(true);

    try {
      // Find provider management record and process response
      const pmResponse = await getProviderManagementAPI(getAuthToken());
      setProvider(pmResponse.provider);
      setDocuments(pmResponse.documents);
      if (pmResponse.payment_preferences?.length) {
        setPaymentPreference(pmResponse.payment_preferences[0].provider_payment_info);
      }

      setIsLoading(false);

    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
      setIsLoading(false);
    }
  }, []);

  const handleCompleteTabs = (tab: "details" | "contact_information" | "payment_remittance", isComplete: boolean) => {
    setCompleteTabs({
      ...completeTabs,
      [tab]: isComplete,
    });
    if (isComplete) {
      onExpandedIdsChange(
        expandedIds.filter((k) => k !== tab),
      );
    }
  };

  const cancelEdit = (tab: "details" | "contact_information" | "payment_remittance") => {
    if (
      (tab === "details" && checkCompleteDetails(provider))
      || (tab === "contact_information" && checkCompleteContactInformation(provider))
      || (tab === "payment_remittance" && checkCompletePaymentRemittance(paymentPreference))
    ) {
      handleCompleteTabs(tab, true);
    }
  };

  const readyToSubmit = () => {
    return (
      completeTabs.details
      && completeTabs.contact_information
      && completeTabs.payment_remittance
      && ["draft", "in progress"].includes(provider.enrollment_status)
    );
  };

  return (
    <>
      {isLoading
      && <Stack className={styles.LoadingContainer} spacing="small">
        <Heading className={styles.LoadingText}>Loading your account details...</Heading>
        <Spinner className={styles.LoadingSpinner} size="large" />
      </Stack>}

      {(!isLoading && provider && provider.enrollment_status === "in_review") && (
        <div className={styles.ReviewMessageContainer}>
          <Stack spacing="medium-large">
            <Title>Provider Profile Submitted for Review</Title>
            {provider.submitted_date
              && <Semibold>Submitted on: {formatDate(provider.submitted_date, "MM/DD/YYYY", true)}</Semibold>
            }
            <Body>
              You can expect an email in 5-10 business days approving your enrollment or alerting you to any issues with the information provided.
            </Body>
            <Link to={getThemeURL("HOME")} style={{ color: primary_color, padding: "4px" }}>
              Return Home
            </Link>
          </Stack>
        </div>
      )}

      {(!isLoading && provider && provider.enrollment_status !== "in_review") && (
        <Stack spacing={isCompressed ? "medium-large" : "large"}>
          {provider.enrollment_status !== "active" && (
            <>
              <BackLink linkText="Home" />
              <Stack>
                <Title>Provider Profile</Title>
                <Body>Complete and save each form section to submit for review</Body>
              </Stack>
              <Stack>
                <CollapseGroup
                  alignCaret="left"
                  expandedIds={expandedIds}
                  onExpandedIdsChange={onExpandedIdsChange}
                  collapseComponent={Collapse.Panel}
                >
                  <CollapseGroup.Collapse
                    id="details"
                    testId="details"
                    title={
                      <Stack>
                        <Shelf>
                          <Subtitle>Provider Details</Subtitle>
                          <Shelf.Spacer />
                          {completeTabs.details && CompleteTag}
                        </Shelf>
                      </Stack>
                    }
                  >
                    <Details
                      provider={provider}
                      documents={documents}
                      setProvider={setProvider}
                      onComplete={() => handleCompleteTabs("details", true)}
                      onCancel={() => cancelEdit("details")}
                      onEdit={() => handleCompleteTabs("details", false)}
                      type={completeTabs.details ? "complete" : "edit"}
                    />
                  </CollapseGroup.Collapse>
                  <CollapseGroup.Collapse
                    title={
                      <Stack>
                        <Shelf>
                          <Subtitle>Contact Information</Subtitle>
                          <Shelf.Spacer />
                          {completeTabs.contact_information && CompleteTag}
                        </Shelf>
                      </Stack>
                    }
                    id="contact_information"
                    testId="contact_information"
                  >
                    <ContactInformation
                      provider={provider}
                      setProvider={setProvider}
                      onComplete={() => handleCompleteTabs("contact_information", true)}
                      onCancel={() => cancelEdit("contact_information")}
                      onEdit={() => handleCompleteTabs("contact_information", false)}
                      type={completeTabs.contact_information ? "complete" : "edit"}
                    />
                  </CollapseGroup.Collapse>
                  <CollapseGroup.Collapse
                    title={
                      <Stack>
                        <Shelf>
                          <Subtitle>Network & Payment Preferences</Subtitle>
                          <Shelf.Spacer />
                          {completeTabs.payment_remittance && CompleteTag}
                        </Shelf>
                      </Stack>
                    }
                    id="payment_remittance"
                    testId="payment_remittance"
                  >
                    <PaymentRemittance
                      provider={provider}
                      documents={documents}
                      paymentPreference={paymentPreference}
                      setPaymentPreference={setPaymentPreference}
                      onComplete={() => handleCompleteTabs("payment_remittance", true)}
                      onCancel={() => cancelEdit("payment_remittance")}
                      type={completeTabs.payment_remittance ? "complete" : "edit"}
                      onEdit={() => handleCompleteTabs("payment_remittance", false)}
                    />
                  </CollapseGroup.Collapse>
                </CollapseGroup>
              </Stack>
              <Message appearance={Messages.Warning} visible={!readyToSubmit()}>Complete and save all sections to submit for review</Message>
              {readyToSubmit() && <ProviderSubmit onComplete={getProvider}/>}
            </>
          )}
          {provider.enrollment_status === "active" && (
            <div className={styles.TabContainer}>
              <Stack spacing="medium-large">
                <Stack spacing="none">
                  <BackLink linkText="Home" />
                  <Title>Provider Profile</Title>
                </Stack>
                <ProviderTabs />
              </Stack>
            </div>
          )}
        </Stack>
      )}
    </>
  );
};
